import {
  getAppleLoginUrl as coreGetAppleLoginUrl,
  getAppleMpcLoginUrl as coreGetAppleMpcLoginUrl,
} from '@polaris/core/auth'
import { connectLitClient, litClient } from '@polaris/core/mpc'
import { isValidUrl } from '@polaris/core/utils'
import { apiClient } from '../client'
import type { AuthCredentials } from './google'
import { type MpcLoginRequest, mpcLogin } from './mpc'

/**
 * Returns a URL to start the Apple OAuth2 flow (legacy backend flow).
 * Uses response_mode=form_post which POSTs to the backend.
 */
export function getAppleLoginUrl(): string {
  const appleClientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID
  if (!appleClientId) {
    throw new Error(
      'NEXT_PUBLIC_APPLE_CLIENT_ID is not defined in environment variables',
    )
  }
  const appBaseUrl = process.env.APP_BASE_URL
  if (!appBaseUrl) {
    throw new Error('APP_BASE_URL is not defined in environment variables')
  }
  if (!isValidUrl(appBaseUrl)) {
    throw new Error('APP_BASE_URL is not a valid URL')
  }

  // Use the API base URL for the redirect
  // In production/staging, this should be the backend API URL
  // For local development, this should be your local backend server
  const apiBaseUrl = process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI || ''
  if (!isValidUrl(apiBaseUrl)) {
    throw new Error('NEXT_PUBLIC_APPLE_REDIRECT_URI is not a valid URL')
  }

  return coreGetAppleLoginUrl({ appleClientId, appBaseUrl, apiBaseUrl })
}

/**
 * Returns a URL to start the Apple OAuth2 flow for MPC.
 * Uses response_mode=query which redirects back to the frontend with ?code=...
 * This allows the frontend to exchange the code via a Lit Action.
 */
export function getAppleMpcLoginUrl(): string {
  const appleClientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID
  if (!appleClientId) {
    throw new Error(
      'NEXT_PUBLIC_APPLE_CLIENT_ID is not defined in environment variables',
    )
  }
  const appBaseUrl = process.env.APP_BASE_URL
  if (!appBaseUrl) {
    throw new Error('APP_BASE_URL is not defined in environment variables')
  }
  if (!isValidUrl(appBaseUrl)) {
    throw new Error('APP_BASE_URL is not a valid URL')
  }

  return coreGetAppleMpcLoginUrl({ appleClientId, appBaseUrl })
}

export const isAppleLoginConfigured =
  process.env.NEXT_PUBLIC_APPLE_CLIENT_ID !== undefined &&
  process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI !== undefined &&
  process.env.APP_BASE_URL !== undefined &&
  isValidUrl(process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI!) &&
  isValidUrl(process.env.APP_BASE_URL!)

interface AppleLoginRequest {
  token: string
}

interface AppleLoginResponse {
  access_token: string
}

/** Returns a polaris access token for Apple sign-in using a pre-issued token. */
export function appleLogin({
  token,
}: AppleLoginRequest): Promise<AppleLoginResponse> {
  return apiClient
    .post('auth/login/apple', {
      json: {
        // Backend ignores these when token is provided
        redirect_uri: 'placeholder',
        code: 'placeholder',
        token,
      },
    })
    .json<AppleLoginResponse>()
}

export function mpcAppleLogin(
  payload: MpcLoginRequest,
): ReturnType<typeof mpcLogin> {
  return mpcLogin(payload)
}

interface AppleTokenLoginRequest {
  code: string
}

interface AppleTokenLoginResponse {
  access_token: string
  id_token: string
}

/**
 * Exchange Apple OAuth authorization code for access and ID tokens using a Lit Action.
 * This mirrors the Google MPC flow but uses the Apple-specific Lit Action and wrapped key.
 */
export async function getAppleTokenViaLitAction({
  code,
}: AppleTokenLoginRequest): Promise<AppleTokenLoginResponse> {
  // Import LIT_NETWORK dynamically to avoid SSR issues
  const { LIT_NETWORK } = await import('@lit-protocol/constants')

  // Fetch auth credentials from main server (which proxies to lit-server)
  const authCredentials = await apiClient
    .get('auth/credentials')
    .json<AuthCredentials>()

  const { sessionSigs, providerInfo } = authCredentials

  if (!providerInfo.apple) {
    throw new Error('Apple provider is not configured on the auth server')
  }

  const appleProvider = providerInfo.apple

  // Validate we received session signatures from Lit servers
  const sessionSigValues = Object.values(sessionSigs)
  if (sessionSigValues.length === 0) {
    throw new Error('No session signatures received from Lit servers')
  }

  // Connect to the singleton Lit client (reuses existing connection if already connected)
  await connectLitClient()

  // For MPC flow, the redirect URI must match what was used in getAppleMpcLoginUrl()
  // which is APP_BASE_URL + /auth/callback/apple
  const appBaseUrl = process.env.APP_BASE_URL
  if (!appBaseUrl) {
    throw new Error('APP_BASE_URL is not defined in environment variables')
  }
  const redirectUri = `${appBaseUrl}/auth/callback/apple`

  // Execute the Lit Action to sign in with Apple
  const result = await litClient.executeJs({
    sessionSigs,
    ipfsId: appleProvider.ipfsHash,
    jsParams: {
      secret_id: appleProvider.keyID,
      litNetwork: LIT_NETWORK.Datil,
      sessionSig: sessionSigValues[0],
      redirect_uri: redirectUri,
      code,
      ipfsHash: appleProvider.ipfsHash,
    },
  })

  // Parse response - handle both string and object formats
  try {
    const tokenResponse =
      typeof result.response === 'string'
        ? JSON.parse(result.response as string)
        : result.response

    if (!tokenResponse.access_token || !tokenResponse.id_token) {
      throw new Error('Missing required tokens in Apple Lit Action response')
    }

    return {
      access_token: tokenResponse.access_token as string,
      id_token: tokenResponse.id_token as string,
    }
  } catch (error) {
    // TODO: refine error handling once we have sample Apple responses and failure modes.
    // For now, keep the error generic so the UI can show a simple failure message.
    // eslint-disable-next-line no-console
    console.error('Failed to parse Apple Lit Action response:', result, error)
    throw new Error('Apple authentication failed. Please try again.')
  }
}
